<template>
  <section class="clip-browser-container page">
    <!-- TABS -->
    <div class="md-tabs md-alignment-left md-no-transition md-theme-default">
      <div class="md-tabs-navigation md-elevation-0">
        <div class="container">
          <button
          type="button"
          class="md-button md-tab-nav-button md-theme-default"
          :class="{'router-link-exact-active router-link-active md-active': areAllClipsShowing}"
          @click.stop="setFilter('all')">
          <div class="md-ripple">
            <div class="md-button-content">
              {{$t('browser.tabs.totalClips.title', { count: allCount })}} <i class="badge">{{ allCount }}</i>
            </div>
          </div>
        </button>
          <button
          type="button"
          class="md-button md-tab-nav-button md-theme-default"
          :class="{'router-link-exact-active router-link-active md-active': !areAllClipsShowing}"
          @click.stop="setFilter('mine')">
          <div class="md-ripple">
            <div class="md-button-content">
              {{$t('browser.tabs.myClip.title', { count: mineCount })}} <i class="badge">{{ mineCount }}</i>
            </div>
          </div>
        </button>
        </div>
    </div>
    </div>
    <!-- CONTENT -->
    <div class="container">
      <section class="section">
        <div class="clip-browser">
          <div class="search-container">
            <md-field>
              <label class="search-label">{{$t('browser.search.label')}}</label>
              <md-input
                type="search"
                class="search-input"
                aria-label="search"
                aria-describedby="searchbox"
                :placeholder="$t('browser.search.placeholder')"
                v-on:input="submitSearch"
                v-model="searchKey" />
            </md-field>
          </div>
          <div
            v-if="searchKey && !isLoading && !hasClip"
            class="empty-container">
            <i class="fas fa-box-open box-icon"></i>
            <h4>{{$t('common:notFound')}}</h4>
          </div>
          <transition name="fade">
            <article
              v-if="!isLoading"
              class="card-container">
              <template v-for="(clip, index) in clipsData">
                <Card
                  :title="getCardTitle(clip)"
                  :subTitle="getCardSubTitle(clip)"
                  :duration="getCardDuration(clip)"
                  :actionButton1="getCardActionButton(clip)"
                  :key="index">
                </Card>
              </template>
              <div class="flex-after" v-if="shouldAddFlexAfter"></div>
            </article>
          </transition>
          <div
            v-if="clipLastPage > 1"
            class="pagination-container">
            <div class="clip-browser__pagination text-center">
              <button
                type="button"
                class="btn btn-link-secondary btn-sm"
                @click="onSelectPage(1)"
                v-if="state.currentPage !== 1">
                <span><i class="fas fa-angle-double-left" /></span>
              </button>
              <button
                type="button"
                class="btn btn-link-secondary btn-sm"
                @click="onPrevPage"
                v-if="state.currentPage !== 1">
                <span><i class="fas fa-chevron-left" /></span>
              </button>
              <template v-if="clipCurrentPage - 3 <= allCount && allCount <= clipCurrentPage + 3">
                <button
                  type="button"
                  class="btn"
                  v-for="count in clipLastPage"
                  :key="'count' + count"
                  :class="{
                    'btn-secondary btn-sm': count === clipLastPage,
                    'btn-link-secondary btn-sm': count !== clipLastPage
                  }"
                  @click="onSelectPage(count)">
                  <span>{{ count }}</span>
                </button>
              </template>
              <button
                type="button"
                class="btn btn-link-secondary btn-sm"
                @click="onNextPage"
                v-if="state.currentPage !== clipLastPage">
                <span><i class="fas fa-chevron-right" /></span>
              </button>
              <button
                type="button"
                class="btn btn-link-secondary btn-sm"
                @click="onSelectPage(clipLastPage)"
                v-if="state.currentPage !== clipLastPage">
                <span><i class="fas fa-angle-double-right" /></span>
              </button>
            </div>
          </div>
        </div>
        <div
          v-if="!hasClip && !isLoading && !searchKey"
          class="empty-container">
          <i class="fas fa-box-open box-icon"></i>
          <h4>{{$t('browser.noClip')}}</h4>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import _ from 'lodash';
import { loadClips, loadAllClips, loadChannels } from '@/api/TimeMachine';
import { prettyFormatPeriod, parseDuration } from '@/utils';
import Card from '@/components/elements/Card.vue';

export default {
  data() {
    return {
      state: {
        isLoading: false,
        activeFilter: 'all',
        currentPage: 0,
      },
      mineCount: 0,
      allCount: 0,
      searchKey: '',
      clips: null,
      channels: [],
    };
  },
  computed: {
    baseURL() {
      return process.env.APP_ORIGIN || window.location.origin;
    },
    isLastPage() {
      return this.clips.currentPage === this.clips.lastPage;
    },
    isFirstPage() {
      return this.clips.currentPage === 1;
    },
    isLoading() {
      return this.state.isLoading;
    },
    clipsData() {
      if (this.clips) {
        return this.clips.data;
      }
      return null;
    },
    shouldAddFlexAfter() {
      return this.clipsData && this.clipsData.length % 3 === 2;
    },
    clipLastPage() {
      if (this.clips) {
        return this.clips.lastPage;
      }
      return 1;
    },
    clipCurrentPage() {
      if (this.clips) {
        return this.clips.currentPage;
      }
      return 1;
    },
    areAllClipsShowing() {
      return this.state.activeFilter === 'all';
    },
    hasClip() {
      if (this.clips) {
        return this.clips.data.length > 0;
      }
      return false;
    },
  },
  components: {
    Card,
  },
  async created() {
    await this.loadChannels();
    await this.initClips();
  },
  methods: {
    prettyFormatPeriod,
    parseDuration,
    getCardTitle(clip) {
      return clip.title || this.getChannelNameById(clip.channelId);
    },
    getCardSubTitle(clip) {
      if (clip.startedAt) {
        return prettyFormatPeriod(clip.startedAt, clip.endedAt);
      }
      return '';
    },
    getCardDuration(clip) {
      if (clip.startedAt) {
        return parseDuration(clip.duration);
      }
      return '';
    },
    getCardActionButton(clip) {
      const button = {};
      if (!clip.availabledAt) {
        button.text = this.$t('common:processing');
        button.action = null;
        button.link = null;
        button.disabled = true;
      } else {
        button.text = this.$t('common:download');
        button.action = null;
        button.link = this.getFileURL(clip);
        button.disabled = false;
      }
      return button;
    },
    async loadChannels() {
      this.state.isLoading = true;
      const response = await loadChannels();
      this.channels = response.data;
      this.state.isLoading = false;
    },
    async initClips() {
      this.state.isLoading = true;
      const response = await loadClips({ limit: 15 });
      const responseAll = await loadAllClips();
      this.clips = response.data;
      this.mineCount = this.clips.total;
      this.allClips = responseAll.data;
      this.allCount = this.allClips.total;
      this.state.currentPage = 1;
      this.state.isLoading = false;
    },
    async loadClips(page = 0, searchKey) {
      this.state.isLoading = true;
      let response;
      if (this.state.activeFilter === 'mine') {
        response = await loadClips({ page, limit: 15, q: searchKey });
      } else {
        response = await loadAllClips({ page, limit: 15, q: searchKey });
      }
      this.clips = response.data;
      this.state.currentPage = page;
      this.state.isLoading = false;
    },
    getFileURL(clip) {
      return `${this.baseURL}/api/clips/${clip._id}/files/video.mp4`;
    },
    getChannelNameById(channelId) {
      const channel = _.find(this.channels, item => item._id === channelId);
      return channel ? channel.name : 'Unknown Channel';
    },
    onPrevPage() {
      this.loadClips(this.clips.currentPage - 1);
    },
    onNextPage() {
      this.loadClips(this.clips.currentPage + 1);
    },
    onSelectPage(page) {
      this.loadClips(page, this.searchKey);
    },
    setFilter(filter) {
      this.state.activeFilter = filter;
      this.searchKey = '';
      this.loadClips(1, this.searchKey);
    },
    submitSearch() {
      this.loadClips(1, this.searchKey);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/themes/transition.scss";
@import '~@/assets/scss/variables.scss';

.flex-after {
  flex: 0 0 32.333%;
}

.empty-container {
  width: 100%;
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-flow: column;
  .box-icon {
    font-size: 72px;
    margin-bottom: $spacer;
  }
}

.md-tabs {
  &.md-theme-default {
    .md-tabs-navigation {
      .md-button {
        color: #FFF;
        .md-button-content {
          display: flex;
          justify-content: center;
          align-items: center;
          .badge {
            width: 19px;
            height: 19px;
            display: flex;
            justify-content: center;
            align-items: center;
            margin-left: 6px;
            background: rgba(255, 255, 255, 0.3);
            border-radius: 100%;
            color: #fff;
            font-style: normal;
          }
        }
        &.md-active {
          color: $primary;
          font-weight: 700;
          border-bottom: 2px solid $primary;
          .badge {
            background: $primary;
          }
        }
        &:focus {
          outline: 0;
        }
      }
    }
  }
}

.card-container {
  display: flex;
  justify-content: space-between;
  flex-flow: row;
  flex-wrap: wrap;
  .md-card {
    flex: 0 0 32.333%;
    margin-bottom: $spacer;
    p {
      margin-bottom: 0;
    }
    &.md-theme-default {
      background-color: $dark-secondary;
    }
  }
}

@media screen and (max-width: 992px) {
  .card-container {
    .md-card {
      flex: 0 0 49%;
    }
  }
}

@media screen and (max-width: 640px) {
  .card-container {
    .md-card {
      flex: 0 0 100%;
    }
  }
}
</style>
